// ABOUTME: Agent configuration panel with system prompt, tools, model, and behavior settings.
// ABOUTME: Full prompt editing with modal editors for tools, clean tabbed layout.

import { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Code,
  MessageSquare,
  Sliders,
  History,
  RotateCcw,
  Save,
  AlertTriangle,
  Plus,
  X,
  Edit,
  Cpu,
  Eye,
  ChevronDown,
  ChevronUp,
  CheckCircle,
} from 'lucide-react';
import { agentConfigApi, workspaceApi } from '@/services/api/adminClient';
import type {
  AgentConfig,
  AgentConfigDefaults,
  SystemPromptConfig,
  ToolPromptConfig,
  ModelConfig,
  BehaviorConfig,
  AgentFeatures,
  ConfigVersionSummary,
  ConfigDiff,
  ToolDefault,
} from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PromptEditorModal, SystemPromptEditorModal } from './PromptEditorModal';

interface AgentConfigPanelProps {
  workspaceId: string;
}

type ConfigTab = 'prompt' | 'tools' | 'model' | 'behavior' | 'history';

const TABS: { id: ConfigTab; label: string; icon: typeof MessageSquare }[] = [
  { id: 'prompt', label: 'System Prompt', icon: MessageSquare },
  { id: 'tools', label: 'Tools', icon: Code },
  { id: 'model', label: 'Model', icon: Cpu },
  { id: 'behavior', label: 'Behavior', icon: Sliders },
  { id: 'history', label: 'History', icon: History },
];

export function AgentConfigPanel({ workspaceId }: AgentConfigPanelProps) {
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [defaults, setDefaults] = useState<AgentConfigDefaults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ConfigTab>('prompt');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Edit state
  const [systemPrompt, setSystemPrompt] = useState<SystemPromptConfig>({
    enabled: true,
    content: '',
    variables: [],
  });
  const [toolPrompts, setToolPrompts] = useState<Record<string, ToolPromptConfig>>({});
  const [modelConfig, setModelConfig] = useState<ModelConfig>({
    default_provider: 'anthropic',
    default_model: 'claude-sonnet-4-5-20250929',
    fallback_model: 'claude-sonnet-4-5-20250929',
    temperature: 0.1,
    max_tokens: 4096,
    model_routing: {
      simple_queries: 'claude-sonnet-4-5-20250929',
      complex_analysis: 'claude-sonnet-4-5-20250929',
      report_generation: 'claude-sonnet-4-5-20250929',
    },
  });
  const [behavior, setBehavior] = useState<BehaviorConfig>({
    hallucination_strictness: 0.9,
    show_reasoning: true,
    auto_execute_queries: true,
    cost_confirmation_threshold: 0.10,
    max_sql_retries: 3,
    glossary: {},
  });
  const [features, setFeatures] = useState<AgentFeatures>({
    sql_execution: true,
    python_execution: true,
    visualization_generation: true,
    schema_discovery: true,
    cost_estimation: true,
    clarification_questions: true,
  });
  const [changeSummary, setChangeSummary] = useState('');

  // Modal state
  const [showSystemPromptEditor, setShowSystemPromptEditor] = useState(false);
  const [editingToolId, setEditingToolId] = useState<string | null>(null);


  // History state
  const [history, setHistory] = useState<ConfigVersionSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<[number, number] | null>(null);
  const [diff, setDiff] = useState<ConfigDiff[]>([]);
  const [diffLoading, setDiffLoading] = useState(false);

  // Pending changes preview state
  const [showPendingChanges, setShowPendingChanges] = useState(false);

  // Get detailed list of changed fields with values
  type ChangeItem = { label: string; from?: string; to?: string };
  const getChangedFields = useCallback((): ChangeItem[] => {
    if (!config) return [];
    const items: ChangeItem[] = [];

    if (JSON.stringify(systemPrompt) !== JSON.stringify(config.system_prompt)) {
      items.push({ label: 'System Prompt' });
    }

    Object.keys(toolPrompts).forEach((toolId) => {
      const current = toolPrompts[toolId];
      const saved = config.tool_prompts[toolId];
      if (JSON.stringify(current) !== JSON.stringify(saved)) {
        const toolName = toolId.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        if (current.enabled !== saved?.enabled) {
          items.push({
            label: toolName,
            from: (saved?.enabled ?? true) ? 'On' : 'Off',
            to: current.enabled ? 'On' : 'Off',
          });
        }
        if (current.description_override !== saved?.description_override) {
          items.push({ label: `${toolName} description` });
        }
      }
    });

    if (JSON.stringify(behavior) !== JSON.stringify(config.behavior)) {
      const behaviorKeys = Object.keys(behavior) as (keyof BehaviorConfig)[];
      behaviorKeys.forEach((key) => {
        if (JSON.stringify(behavior[key]) !== JSON.stringify(config.behavior[key])) {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
          const oldVal = config.behavior[key];
          const newVal = behavior[key];
          items.push({
            label,
            from: typeof oldVal === 'boolean' ? (oldVal ? 'On' : 'Off') : String(oldVal),
            to: typeof newVal === 'boolean' ? (newVal ? 'On' : 'Off') : String(newVal),
          });
        }
      });
    }

    if (JSON.stringify(features) !== JSON.stringify(config.agent_features)) {
      const featureKeys = Object.keys(features) as (keyof AgentFeatures)[];
      featureKeys.forEach((key) => {
        if (features[key] !== config.agent_features[key]) {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
          items.push({
            label,
            from: config.agent_features[key] ? 'On' : 'Off',
            to: features[key] ? 'On' : 'Off',
          });
        }
      });
    }

    return items;
  }, [config, systemPrompt, toolPrompts, behavior, features]);

  // Load config and defaults
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [configData, defaultsData] = await Promise.all([
          agentConfigApi.get(workspaceId),
          agentConfigApi.getDefaults(),
        ]);
        setConfig(configData);
        setDefaults(defaultsData);
        initializeFromConfig(configData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load config');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [workspaceId]);

  const initializeFromConfig = useCallback((data: AgentConfig) => {
    setSystemPrompt(data.system_prompt);
    setToolPrompts(data.tool_prompts);
    setModelConfig(data.model_config);
    setBehavior(data.behavior);
    setFeatures(data.agent_features);
    setHasChanges(false);
  }, []);

  // Load history when tab changes
  useEffect(() => {
    if (activeTab === 'history' && history.length === 0) {
      loadHistory();
    }
  }, [activeTab]);

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const data = await agentConfigApi.getHistory(workspaceId, 20);
      setHistory(data.versions);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSave = async () => {
    if (!changeSummary.trim()) {
      setError('Please provide a change summary');
      return;
    }

    try {
      setSaving(true);
      const updated = await agentConfigApi.update(workspaceId, {
        change_summary: changeSummary,
        system_prompt: systemPrompt,
        tool_prompts: toolPrompts,
        model_config: modelConfig,
        behavior,
        agent_features: features,
      });
      setConfig(updated);
      setHasChanges(false);
      setChangeSummary('');
      loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async (version: number) => {
    try {
      setSaving(true);
      const rolled = await agentConfigApi.rollback(workspaceId, version);
      setConfig(rolled);
      initializeFromConfig(rolled);
      loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rollback');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      const reset = await agentConfigApi.reset(workspaceId);
      setConfig(reset);
      initializeFromConfig(reset);
      loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset config');
    } finally {
      setSaving(false);
    }
  };

  const handleCompareVersions = async (v1: number, v2: number) => {
    try {
      setDiffLoading(true);
      setSelectedVersions([v1, v2]);
      const result = await agentConfigApi.getDiff(workspaceId, v1, v2);
      setDiff(result.diffs);
    } catch (err) {
      console.error('Failed to load diff:', err);
    } finally {
      setDiffLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />
        {error}
        <Button variant="ghost" size="sm" onClick={() => setError(null)}>
          Dismiss
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-surface-800">Agent Configuration</h2>
            <p className="text-sm text-surface-500">
              Version {config?.version || 0}
              {hasChanges && <span className="text-warning ml-2">• Unsaved changes</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                if (config) initializeFromConfig(config);
              }}
              disabled={!hasChanges}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              leftIcon={<Save className="w-4 h-4" />}
              onClick={handleSave}
              isLoading={saving}
              disabled={!hasChanges}
            >
              Save
            </Button>
          </div>
        </div>

        {/* Change Summary Input */}
        {hasChanges && (
          <div className="mt-4">
            <Input
              label="Change Summary (required)"
              placeholder="Describe what you changed..."
              value={changeSummary}
              onChange={(e) => setChangeSummary(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Pending Changes Preview */}
      {hasChanges && (
        <Card variant="surface" className="border border-warning/30 bg-warning-light">
          <button
            onClick={() => setShowPendingChanges(!showPendingChanges)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-warning" />
              <span className="text-sm font-medium text-surface-800">
                Review Changes ({getChangedFields().length})
              </span>
            </div>
            {showPendingChanges ? (
              <ChevronUp className="w-4 h-4 text-surface-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-surface-500" />
            )}
          </button>

          {showPendingChanges && (
            <div className="mt-3 space-y-2">
              {getChangedFields().map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 rounded-sm bg-surface-50 border border-surface-200"
                >
                  <span className="text-sm text-surface-800">{item.label}</span>
                  {item.from && item.to && (
                    <div className="flex items-center gap-1.5 text-xs">
                      <span className={item.from === 'On' ? 'text-success' : 'text-error'}>
                        {item.from}
                      </span>
                      <span className="text-surface-500">→</span>
                      <span className={item.to === 'On' ? 'text-success' : 'text-error'}>
                        {item.to}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-200 pb-2 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-50 text-primary-600'
                : 'text-surface-600 hover:text-surface-800 hover:bg-surface-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="pb-8">
        {activeTab === 'prompt' && (
          <SystemPromptTab
            systemPrompt={systemPrompt}
            defaultPrompt={defaults?.system_prompt || ''}
            onUpdate={(updates) => {
              setSystemPrompt((prev) => ({ ...prev, ...updates }));
              setHasChanges(true);
            }}
            onOpenEditor={() => setShowSystemPromptEditor(true)}
          />
        )}

        {activeTab === 'tools' && (
          <ToolsTab
            toolPrompts={toolPrompts}
            toolDefaults={defaults?.tool_defaults || []}
            features={features}
            onUpdateTool={(toolId, updates) => {
              setToolPrompts((prev) => ({
                ...prev,
                [toolId]: { ...prev[toolId], ...updates },
              }));
              setHasChanges(true);
            }}
            onUpdateFeatures={(updates) => {
              setFeatures((prev) => ({ ...prev, ...updates }));
              setHasChanges(true);
            }}
            onEditToolPrompt={setEditingToolId}
          />
        )}

        {activeTab === 'model' && (
          <ModelTab
            workspaceId={workspaceId}
            modelConfig={modelConfig}
            onUpdate={(updates) => {
              setModelConfig((prev) => ({ ...prev, ...updates }));
              setHasChanges(true);
            }}
          />
        )}

        {activeTab === 'behavior' && (
          <BehaviorTab
            behavior={behavior}
            defaultBehavior={defaults?.behavior_defaults}
            onUpdate={(updates) => {
              setBehavior((prev) => ({ ...prev, ...updates }));
              setHasChanges(true);
            }}
          />
        )}

        {activeTab === 'history' && (
          <HistoryTab
            history={history}
            loading={historyLoading}
            diff={diff}
            diffLoading={diffLoading}
            selectedVersions={selectedVersions}
            onRollback={handleRollback}
            onReset={handleReset}
            onCompare={handleCompareVersions}
          />
        )}
      </div>

      {/* System Prompt Editor Modal */}
      <SystemPromptEditorModal
        isOpen={showSystemPromptEditor}
        onClose={() => setShowSystemPromptEditor(false)}
        value={systemPrompt.content}
        onChange={(content) => {
          setSystemPrompt((prev) => ({ ...prev, content }));
          setHasChanges(true);
        }}
        defaultValue={defaults?.system_prompt || ''}
      />

      {/* Tool Prompt Editor Modal */}
      {editingToolId && (() => {
        const toolDefault = defaults?.tool_defaults.find((t) => t.id === editingToolId);
        const toolDef = toolPrompts[editingToolId]?.tool_definition || toolDefault?.tool_definition || null;
        const defaultDesc = toolDef?.description || toolDefault?.description || '';

        return (
          <PromptEditorModal
            isOpen={!!editingToolId}
            onClose={() => setEditingToolId(null)}
            title={toolDef?.name || editingToolId.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            value={toolPrompts[editingToolId]?.description_override || ''}
            onChange={(value) => {
              setToolPrompts((prev) => ({
                ...prev,
                [editingToolId]: {
                  ...prev[editingToolId],
                  description_override: value || null,
                },
              }));
              setHasChanges(true);
            }}
            defaultValue={defaultDesc}
            toolDefinition={toolDef}
          />
        );
      })()}
    </div>
  );
}

// System Prompt Tab Component
interface SystemPromptTabProps {
  systemPrompt: SystemPromptConfig;
  defaultPrompt: string;
  onUpdate: (updates: Partial<SystemPromptConfig>) => void;
  onOpenEditor: () => void;
}

function SystemPromptTab({ systemPrompt, defaultPrompt, onOpenEditor }: SystemPromptTabProps) {
  const [defaultPromptExpanded, setDefaultPromptExpanded] = useState(false);
  const hasCustomInstructions = systemPrompt.content && systemPrompt.content.trim().length > 0;

  // Shared markdown components
  const markdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="text-surface-600 leading-relaxed mb-3 last:mb-0">
        {children}
      </p>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <strong className="text-surface-800 font-semibold">{children}</strong>
    ),
    h1: ({ children }: { children?: React.ReactNode }) => (
      <h1 className="text-lg font-bold text-surface-800 mb-3 mt-4 first:mt-0">{children}</h1>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <h2 className="text-base font-semibold text-surface-800 mb-2 mt-3 first:mt-0">{children}</h2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <h3 className="text-sm font-semibold text-surface-800 mb-2 mt-3 first:mt-0">{children}</h3>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="list-disc list-inside space-y-1 text-surface-600 mb-3 ml-2">
        {children}
      </ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="list-decimal list-inside space-y-1 text-surface-600 mb-3 ml-2">
        {children}
      </ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => <li className="text-surface-600">{children}</li>,
    code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
      const isInline = !className;
      if (isInline) {
        return (
          <code className="px-1.5 py-0.5 rounded-sm bg-surface-100 text-primary-600 font-mono text-xs">
            {children}
          </code>
        );
      }
      return (
        <code className="block p-3 rounded-sm bg-surface-50 border border-surface-200 font-mono text-xs overflow-x-auto">
          {children}
        </code>
      );
    },
    pre: ({ children }: { children?: React.ReactNode }) => <div className="my-3">{children}</div>,
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="border-l-2 border-primary-600/50 pl-4 my-3 text-surface-500 italic">
        {children}
      </blockquote>
    ),
  };

  return (
    <div className="space-y-4">
      {/* Default System Prompt - Read Only Accordion */}
      <Card variant="surface" className="p-0 overflow-hidden">
        <button
          onClick={() => setDefaultPromptExpanded(!defaultPromptExpanded)}
          className="w-full flex items-center justify-between px-4 py-3 border-b border-surface-200 bg-surface-50 hover:bg-surface-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-surface-800">Default System Prompt</h3>
                <span className="px-2 py-0.5 text-[10px] bg-surface-200 text-surface-600 rounded-sm font-medium uppercase tracking-wide">
                  Read Only
                </span>
              </div>
              <p className="text-xs text-surface-500 mt-0.5">
                Base instructions from codebase • {defaultPrompt.length.toLocaleString()} characters
              </p>
            </div>
          </div>
          {defaultPromptExpanded ? (
            <ChevronUp className="w-5 h-5 text-surface-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-surface-500" />
          )}
        </button>

        {defaultPromptExpanded && (
          <div className="p-4 text-sm text-surface-600 max-h-[40vh] overflow-y-auto prose prose-sm max-w-none bg-surface-50/50">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {defaultPrompt}
            </ReactMarkdown>
          </div>
        )}
      </Card>

      {/* Dataset-Specific Custom Instructions - Editable */}
      <Card variant="surface" className="p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 bg-surface-50">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-surface-800">Dataset-Specific Instructions</h3>
                {hasCustomInstructions && (
                  <span className="px-2 py-0.5 text-[10px] bg-primary-50 text-primary-600 rounded-sm font-medium uppercase tracking-wide">
                    Custom
                  </span>
                )}
              </div>
              <p className="text-xs text-surface-500 mt-0.5">
                {hasCustomInstructions
                  ? `${systemPrompt.content.length.toLocaleString()} characters • Appended to default prompt`
                  : 'No custom instructions configured'}
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Edit className="w-4 h-4" />}
            onClick={onOpenEditor}
          >
            Edit
          </Button>
        </div>

        {/* Instructions hint */}
        <div className="px-4 py-3 bg-primary-50/50 border-b border-primary-100">
          <p className="text-xs text-primary-700">
            <strong>These instructions are dataset-specific.</strong> Add context about your data schema,
            table structures, query patterns, business logic, and domain knowledge here.
            This content is appended to the default system prompt above.
          </p>
        </div>

        {/* Preview or empty state */}
        {hasCustomInstructions ? (
          <div className="p-4 text-sm text-surface-600 max-h-[40vh] overflow-y-auto prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {systemPrompt.content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-sm text-surface-500 mb-3">
              No dataset-specific instructions configured yet.
            </p>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={onOpenEditor}
            >
              Add Custom Instructions
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

// Tools Tab Component
interface ToolsTabProps {
  toolPrompts: Record<string, ToolPromptConfig>;
  toolDefaults: ToolDefault[];
  features: AgentFeatures;
  onUpdateTool: (toolId: string, updates: Partial<ToolPromptConfig>) => void;
  onUpdateFeatures: (updates: Partial<AgentFeatures>) => void;
  onEditToolPrompt: (toolId: string) => void;
}

function ToolsTab({
  toolPrompts,
  toolDefaults,
  features,
  onUpdateTool,
  onUpdateFeatures,
  onEditToolPrompt,
}: ToolsTabProps) {
  // Map tool IDs to their corresponding feature keys
  const featureMap: Record<string, keyof AgentFeatures> = {
    execute_sql: 'sql_execution',
    execute_python: 'python_execution',
    discover_schema: 'schema_discovery',
    estimate_cost: 'cost_estimation',
  };

  return (
    <Card variant="surface" className="p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-surface-200">
        <h3 className="text-sm font-medium text-surface-800">Tools</h3>
        <p className="text-xs text-surface-500 mt-1">Configure agent capabilities and tool prompts</p>
      </div>
      <div className="divide-y divide-surface-100">
        {toolDefaults.map((tool) => {
          const toolConfig = toolPrompts[tool.id] || { enabled: true, description_override: null, additional_instructions: null };
          const featureKey = featureMap[tool.id];
          const hasCustomPrompt = !!toolConfig.description_override;
          const isEnabled = featureKey ? features[featureKey] : toolConfig.enabled;

          return (
            <div
              key={tool.id}
              className={`flex items-center justify-between px-5 py-3.5 transition-colors ${
                isEnabled ? 'hover:bg-surface-50' : 'opacity-50'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="text-sm text-surface-800 font-medium">
                  {tool.name}
                </span>
                {hasCustomPrompt && (
                  <span className="px-2 py-0.5 text-[10px] bg-primary-50 text-primary-600 rounded-sm font-medium uppercase tracking-wide">
                    Custom
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <StatusToggle
                  checked={isEnabled}
                  onChange={(v) => {
                    if (featureKey) {
                      onUpdateFeatures({ [featureKey]: v });
                    } else {
                      onUpdateTool(tool.id, { enabled: v });
                    }
                  }}
                />
                <button
                  onClick={() => onEditToolPrompt(tool.id)}
                  disabled={!isEnabled}
                  className={`px-3 py-1 text-xs font-medium rounded-sm transition-all ${
                    isEnabled
                      ? 'bg-surface-100 text-surface-600 hover:bg-surface-200 hover:text-surface-800'
                      : 'bg-transparent text-surface-400 cursor-not-allowed'
                  }`}
                >
                  Edit Prompt
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-primary-600' : 'bg-surface-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// Fancy status toggle with On/Off labels
function StatusToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-7 w-[72px] items-center rounded-sm bg-surface-50 border border-surface-200 p-0.5 transition-all hover:border-surface-300"
    >
      {/* Sliding background */}
      <span
        className={`absolute h-6 w-8 rounded-sm transition-all duration-200 ease-out ${
          checked
            ? 'left-[calc(100%-34px)] bg-success-light'
            : 'left-0.5 bg-error-light'
        }`}
      />
      {/* Off label */}
      <span
        className={`relative z-10 flex-1 text-center text-[10px] font-semibold uppercase tracking-wider transition-colors ${
          !checked ? 'text-error' : 'text-surface-400'
        }`}
      >
        Off
      </span>
      {/* On label */}
      <span
        className={`relative z-10 flex-1 text-center text-[10px] font-semibold uppercase tracking-wider transition-colors ${
          checked ? 'text-success' : 'text-surface-400'
        }`}
      >
        On
      </span>
    </button>
  );
}

// Model Tab Component
interface ModelTabProps {
  workspaceId: string;
  modelConfig: ModelConfig;
  onUpdate: (updates: Partial<ModelConfig>) => void;
}

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
];

function formatModelName(modelId: string): string {
  const opt = MODEL_OPTIONS.find((o) => o.value === modelId);
  return opt?.label || modelId;
}

function ModelTab({ workspaceId, modelConfig, onUpdate }: ModelTabProps) {
  const [editing, setEditing] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [apiKeySuccess, setApiKeySuccess] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      setApiKeyError('API key is required');
      return;
    }

    try {
      setApiKeySaving(true);
      setApiKeyError(null);
      setApiKeySuccess(false);
      await workspaceApi.setAnthropicApiKey(workspaceId, apiKey.trim());
      setApiKeySuccess(true);
      setApiKey('');
      setTimeout(() => setApiKeySuccess(false), 3000);
    } catch (err) {
      setApiKeyError(err instanceof Error ? err.message : 'Failed to save API key');
    } finally {
      setApiKeySaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Model Configuration */}
      <Card variant="surface" className="p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-200 bg-surface-50 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-primary-600" />
              <h3 className="text-sm font-medium text-surface-800">Model Configuration</h3>
            </div>
            <p className="text-xs text-surface-500 mt-1">Which model each feature uses</p>
          </div>
          <button
            onClick={() => setEditing(!editing)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm border border-surface-200 text-surface-600 hover:bg-surface-100 transition-colors"
          >
            <Edit className="w-3.5 h-3.5" />
            {editing ? 'Done' : 'Edit'}
          </button>
        </div>
        <div className="divide-y divide-surface-100">
          {[
            { label: 'Investigations', desc: 'All investigation messages and agent queries', value: modelConfig.model_routing.simple_queries, onChange: (v: string) => onUpdate({ model_routing: { ...modelConfig.model_routing, simple_queries: v, complex_analysis: v } }) },
            { label: 'Reports & Editing', desc: 'Report generation, full edit, magic edit, prompt suggestions', value: modelConfig.model_routing.report_generation, onChange: (v: string) => onUpdate({ model_routing: { ...modelConfig.model_routing, report_generation: v } }) },
            { label: 'Fallback Model', desc: 'Used when no specific routing applies', value: modelConfig.default_model, onChange: (v: string) => onUpdate({ default_model: v }) },
          ].map((row) => (
            <div key={row.label} className="flex items-center justify-between px-5 py-3.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-surface-800">{row.label}</p>
                <p className="text-xs text-surface-400 mt-0.5">{row.desc}</p>
              </div>
              {editing ? (
                <select
                  value={row.value}
                  onChange={(e) => row.onChange(e.target.value)}
                  className="px-3 py-2 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm min-w-[220px]"
                >
                  {!MODEL_OPTIONS.some((o) => o.value === row.value) && (
                    <option value={row.value}>{row.value} (legacy)</option>
                  )}
                  {MODEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <span className="text-sm font-mono text-surface-700">{formatModelName(row.value)}</span>
              )}
            </div>
          ))}
        </div>

        {/* Parameters — only shown when editing */}
        {editing && (
          <div className="px-5 py-4 border-t border-surface-200 bg-surface-50">
            <p className="text-xs font-medium text-surface-500 mb-3">Parameters</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-surface-500 mb-1">Temperature</label>
                <input
                  type="number"
                  min={0}
                  max={2}
                  step={0.1}
                  value={modelConfig.temperature}
                  onChange={(e) => onUpdate({ temperature: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm"
                />
                <p className="text-xs text-surface-400 mt-1">0 = deterministic, 2 = creative</p>
              </div>
              <div>
                <label className="block text-xs text-surface-500 mb-1">Max Tokens</label>
                <input
                  type="number"
                  min={1}
                  max={128000}
                  step={256}
                  value={modelConfig.max_tokens}
                  onChange={(e) => onUpdate({ max_tokens: parseInt(e.target.value) || 4096 })}
                  className="w-full px-3 py-2 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm"
                />
                <p className="text-xs text-surface-400 mt-1">Maximum output length</p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* API Credentials */}
      <Card variant="surface" className="p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-200 bg-surface-50">
          <h3 className="text-sm font-medium text-surface-800">API Credentials</h3>
          <p className="text-xs text-surface-500 mt-1">
            Set the Anthropic API key for this workspace. For security, the current key is not displayed.
          </p>
        </div>
        <div className="p-5 space-y-4">
          {apiKeySuccess && (
            <div className="mb-4 p-3 bg-success-light border border-success/30 rounded-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success" />
              <span className="text-sm text-success">API key saved successfully</span>
            </div>
          )}

          {apiKeyError && (
            <div className="mb-4 p-3 bg-error-light border border-error/30 rounded-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-error" />
              <span className="text-sm text-error">{apiKeyError}</span>
            </div>
          )}

          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-ant-api03-..."
                className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm font-mono"
              />
            </div>
            <Button
              onClick={handleSaveApiKey}
              isLoading={apiKeySaving}
              disabled={!apiKey.trim()}
            >
              <Save className="w-4 h-4 mr-1" />
              Save Key
            </Button>
          </div>
          <p className="text-xs text-surface-500 mt-2">
            The API key will be stored securely and used for all Anthropic model calls in this workspace.
          </p>
        </div>
      </Card>
    </div>
  );
}

// Behavior Tab Component
interface BehaviorTabProps {
  behavior: BehaviorConfig;
  defaultBehavior?: BehaviorConfig;
  onUpdate: (updates: Partial<BehaviorConfig>) => void;
}

function BehaviorTab({ behavior, onUpdate }: BehaviorTabProps) {
  const [newGlossaryKey, setNewGlossaryKey] = useState('');
  const [newGlossaryValue, setNewGlossaryValue] = useState('');

  const addGlossaryEntry = () => {
    if (newGlossaryKey.trim() && newGlossaryValue.trim()) {
      onUpdate({
        glossary: { ...behavior.glossary, [newGlossaryKey.trim()]: newGlossaryValue.trim() },
      });
      setNewGlossaryKey('');
      setNewGlossaryValue('');
    }
  };

  const removeGlossaryEntry = (key: string) => {
    const { [key]: _, ...rest } = behavior.glossary;
    onUpdate({ glossary: rest });
  };

  return (
    <Card variant="surface">
      <div className="space-y-6">
        {/* Strictness Slider */}
        <div>
          <label className="block text-sm font-medium text-surface-600 mb-1.5">
            Hallucination Strictness: {(behavior.hallucination_strictness * 100).toFixed(0)}%
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={behavior.hallucination_strictness}
            onChange={(e) => onUpdate({ hallucination_strictness: parseFloat(e.target.value) })}
            className="w-full accent-primary-600"
          />
          <div className="flex justify-between text-xs text-surface-500 mt-1">
            <span>Lenient</span>
            <span>Strict</span>
          </div>
        </div>

        {/* Toggle Settings */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-surface-800">Show Reasoning</span>
            <ToggleSwitch
              checked={behavior.show_reasoning}
              onChange={(v) => onUpdate({ show_reasoning: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-surface-800">Auto-Execute Queries</span>
            <ToggleSwitch
              checked={behavior.auto_execute_queries}
              onChange={(v) => onUpdate({ auto_execute_queries: v })}
            />
          </div>
        </div>

        {/* Numeric Settings */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-surface-600 mb-1.5">
              Cost Confirmation Threshold ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={behavior.cost_confirmation_threshold}
              onChange={(e) => onUpdate({ cost_confirmation_threshold: parseFloat(e.target.value) })}
              className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-600 mb-1.5">
              Max SQL Retries
            </label>
            <input
              type="number"
              min="0"
              max="10"
              value={behavior.max_sql_retries}
              onChange={(e) => onUpdate({ max_sql_retries: parseInt(e.target.value) })}
              className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm"
            />
          </div>
        </div>

        {/* Glossary Editor */}
        <div>
          <h3 className="text-sm font-medium text-surface-800 mb-3">Business Glossary</h3>
          <p className="text-xs text-surface-500 mb-3">
            Define terms specific to this workspace for the agent to understand
          </p>

          {/* Existing entries */}
          {Object.entries(behavior.glossary).length > 0 && (
            <div className="space-y-2 mb-4">
              {Object.entries(behavior.glossary).map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-center gap-2 p-2 bg-surface-50 rounded-sm"
                >
                  <span className="text-sm font-medium text-primary-600 min-w-[100px]">
                    {key}
                  </span>
                  <span className="text-sm text-surface-600 flex-1">{value}</span>
                  <button
                    onClick={() => removeGlossaryEntry(key)}
                    className="p-1 text-surface-500 hover:text-error"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new entry */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Term"
              value={newGlossaryKey}
              onChange={(e) => setNewGlossaryKey(e.target.value)}
              className="flex-1 px-3 py-2 rounded-sm bg-surface-50 border border-surface-200 text-surface-800 text-sm"
            />
            <input
              type="text"
              placeholder="Definition"
              value={newGlossaryValue}
              onChange={(e) => setNewGlossaryValue(e.target.value)}
              className="flex-[2] px-3 py-2 rounded-sm bg-surface-50 border border-surface-200 text-surface-800 text-sm"
            />
            <Button variant="secondary" size="sm" onClick={addGlossaryEntry}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// History Tab Component
interface HistoryTabProps {
  history: ConfigVersionSummary[];
  loading: boolean;
  diff: ConfigDiff[];
  diffLoading: boolean;
  selectedVersions: [number, number] | null;
  onRollback: (version: number) => void;
  onReset: () => void;
  onCompare: (v1: number, v2: number) => void;
}

function HistoryTab({
  history,
  loading,
  diff,
  diffLoading,
  selectedVersions,
  onRollback,
  onReset,
  onCompare,
}: HistoryTabProps) {
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<number[]>([]);

  const handleSelectForCompare = (version: number) => {
    if (selectedForCompare.includes(version)) {
      setSelectedForCompare((prev) => prev.filter((v) => v !== version));
    } else if (selectedForCompare.length < 2) {
      const newSelection = [...selectedForCompare, version];
      setSelectedForCompare(newSelection);
      if (newSelection.length === 2) {
        onCompare(newSelection[0], newSelection[1]);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant={compareMode ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => {
            setCompareMode(!compareMode);
            setSelectedForCompare([]);
          }}
        >
          {compareMode ? 'Cancel Compare' : 'Compare Versions'}
        </Button>
        <Button
          variant="danger"
          size="sm"
          leftIcon={<RotateCcw className="w-4 h-4" />}
          onClick={onReset}
        >
          Reset to Defaults
        </Button>
      </div>

      {compareMode && selectedForCompare.length > 0 && (
        <p className="text-sm text-surface-500">
          Selected: {selectedForCompare.join(' and ')}
          {selectedForCompare.length === 1 && ' (select one more)'}
        </p>
      )}

      {/* Diff View */}
      {selectedVersions && diff.length > 0 && (
        <Card variant="surface" className="border border-primary-600/30">
          <h3 className="text-sm font-medium text-surface-800 mb-3">
            Diff: Version {selectedVersions[0]} → {selectedVersions[1]}
          </h3>
          {diffLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {diff.map((d, i) => (
                <div key={i} className="text-sm">
                  <p className="font-medium text-surface-800">{d.field}</p>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div className="p-2 bg-error-light rounded-sm text-xs">
                      <span className="text-error">-</span>{' '}
                      {JSON.stringify(d.old_value)}
                    </div>
                    <div className="p-2 bg-success-light rounded-sm text-xs">
                      <span className="text-success">+</span>{' '}
                      {JSON.stringify(d.new_value)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Version List */}
      <div className="space-y-2">
        {history.length === 0 ? (
          <p className="text-sm text-surface-500 text-center py-8">
            No version history available
          </p>
        ) : (
          history.map((version) => (
            <Card key={version.version} variant="surface">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {compareMode && (
                    <input
                      type="checkbox"
                      checked={selectedForCompare.includes(version.version)}
                      onChange={() => handleSelectForCompare(version.version)}
                      disabled={
                        selectedForCompare.length >= 2 &&
                        !selectedForCompare.includes(version.version)
                      }
                      className="rounded-sm border-surface-200"
                    />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-surface-800">
                        Version {version.version}
                      </p>
                      {version.is_active && (
                        <span className="px-2 py-0.5 text-xs bg-success-light text-success rounded-sm">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-surface-500">{version.change_summary}</p>
                    <p className="text-xs text-surface-500">
                      {new Date(version.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                {!version.is_active && !compareMode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<RotateCcw className="w-3 h-3" />}
                    onClick={() => onRollback(version.version)}
                  >
                    Rollback
                  </Button>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

export default AgentConfigPanel;
